
import { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { createReview } from "@/services/reviewService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const reviewSchema = z.object({
  comment: z.string().optional(),
  contactedProvider: z.boolean().default(false)
});

type ReviewFormValues = z.infer<typeof reviewSchema>;

interface ReviewFormProps {
  serviceId: string;
  onReviewSubmit: () => void;
  onCancel: () => void;
}

const ReviewForm = ({ serviceId, onReviewSubmit, onCancel }: ReviewFormProps) => {
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { user } = useAuth();
  
  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      comment: "",
      contactedProvider: false
    }
  });
  
  const handleSubmit = async (values: ReviewFormValues) => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    
    if (!user) {
      toast.error("You must be logged in to submit a review");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Insert the review into the reviews table
      await createReview({
        provider_id: serviceId,
        user_id: user.id,
        rating,
        comment: values.comment || null,
        contacted_provider: values.contactedProvider
      });
      
      toast.success("Your review has been submitted!");
      onReviewSubmit();
    } catch (error: unknown) {
      console.error("Error submitting review:", error);
      const message = error instanceof Error ? error.message : 'Failed to submit review. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="rounded-md border border-brand-cloud bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="public-card-title">Write a Review</CardTitle>
      </CardHeader>
      
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="mb-4">
              <FormLabel className="mb-2 block text-sm font-medium text-brand-ink">
                Rating
              </FormLabel>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className="p-1"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                  >
                    <Star
                      className={`h-7 w-7 transition-colors ${
                        (hoveredRating ? star <= hoveredRating : star <= rating)
                          ? "fill-amber text-amber"
                          : "text-brand-cloud"
                      }`}
                    />
                  </button>
                ))}
              </div>
              {rating === 0 && (
                <p className="text-sm text-red-500 mt-1">
                  Please select a rating
                </p>
              )}
            </div>
            
            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="mb-2 block text-sm font-medium text-brand-ink">
                    Comment (Optional)
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Share your experience with this service provider..."
                      className="min-h-[120px] resize-none rounded-md border-brand-cloud focus-visible:ring-brand-ink"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="contactedProvider"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-brand-cloud bg-brand-mist p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-normal text-brand-slate">
                      I contacted this provider via WhatsApp
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />
            
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                className="flex-1 rounded-md bg-brand-ink px-4 py-2 font-medium text-white transition-colors hover:bg-brand-charcoal"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Review"}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                className="rounded-md border-brand-cloud px-4 py-2 text-brand-ink hover:bg-brand-mist hover:text-brand-ink"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default ReviewForm;
